// Used by: QuoteQueryJets, QuoteQueryHelicopters, QuoteQueryGroupCharter
import { titleList } from "../../data/titleList";
import { currencyList } from "../../data/currencyList";
import SelectInput from "../ui/SelectInput";
import TextInput from "../ui/TextInput";
import Button from "../ui/Button";
import Form from "../ui/Form";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import usePost from "../../hooks/usePost";
import TextAreaInput from "../ui/TextAreaInput";
import Checkbox from "../ui/Checkbox";
import ReturnMessage from "./ReturnMessage";
import LoadingOverlay from "./LoadingOverlay";
import { Link } from "react-router-dom";
import TagManager from "react-gtm-module";
import { formatTimeTo24h, dateStringToApiFormat } from "../../utilities/date";
import poweredByContext from "../../store/PoweredByContext";
import { replaceShyName } from "../../utilities/string";
import { TRIP_TYPE_RETURN_TRIP } from "../../utilities/tripTypeDisplay";

// Icons
import bigTick from "../../assets/icons/tick-big.svg";
import JourneyLeg from "./JourneyLeg";
import QuoteQueryJourneyLeg from "./QuoteQueryJourneyLeg";
import SlideDown from "react-slidedown/lib/slidedown";
import chevronDown from "../../assets/icons/chevron-down-white.svg";
import chevronDownBig from "../../assets/icons/chevron-down-big.svg";
import ContactPreferencesInput from "../manage-passengers/ContactPreferencesInput";
import authContext from "../../store/AuthContext";

const QuoteQueryPersonalDetails = ({
    journeyType,
    journeyData,
    journeyIsValid,
    resetJourney,
    closeModal,
    inModal,
    is24Hour,
}) => {
    // State
    const [validateForm, setValidateForm] = useState(false);
    const [firstName, setFirstName] = useState(titleList[0]["value"]);
    const [surname, setSurname] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [requests, setRequests] = useState("");
    const [preferredCurrency, setPreferredCurrency] = useState("");
    const [contactPreferences, setContactPreferences] = useState({ whatsApp: false, email: true });
    const [gdprConsentGiven, setGdprConsentGiven] = useState(false);
    const [layout, setLayout] = useState("");
    const [slideDownOpen, setSlideDownOpen] = useState(false);
    const [rememberJourneyData, setRememberJourneyData] = useState(false);
    const [showAllLegs, setShowAllLegs] = useState(false);

    // Hooks
    const { sending, returnMessage, postData } = usePost();
    const slideDown = useRef();
    const { search } = useLocation();
    const queryParams = new URLSearchParams(search);
    const poweredByCtx = useContext(poweredByContext);

    // Auth context
    const authCtx = useContext(authContext);

    // Variables
    const legCount = rememberJourneyData.length;

    // Detect if two legs form a true return (same airports swapped)
    const isReturnTwoLegs = () => {
        if (legCount !== 2) return false;
        if (journeyType === "helicopters") return false;
        const a = rememberJourneyData[0];
        const b = rememberJourneyData[1];
        const aFrom = a?.flightFrom?.icao;
        const aTo = a?.flightTo?.icao;
        const bFrom = b?.flightFrom?.icao;
        const bTo = b?.flightTo?.icao;
        if (!aFrom || !aTo || !bFrom || !bTo) return false;
        return aFrom === bTo && aTo === bFrom;
    };

    const replaceName = (str) => {
        return replaceShyName(str, poweredByCtx.isPoweredBy, " ");
    };

    const checkUtmProperties = () => {
        const properties = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
        let values = {};

        properties.forEach((property) => {
            const value = queryParams.get(property);

            if (value) {
                values[property] = value;
            }
        });

        return values;
    };

    const getTime = (time) => (is24Hour ? time : formatTimeTo24h(time));

    // Submit
    const submitHandler = (e) => {
        e.preventDefault();
        setValidateForm(true);

        // Check form complete
        let formComplete = false;
        if (authCtx.token) {
            formComplete = true;
        } else {
            if (firstName && surname && email && phone && preferredCurrency && gdprConsentGiven) {
                formComplete = true;
            }
        }

        // Form complete
        if (formComplete) {
            // Format journey data
            let formattedJourneyData = [];
            rememberJourneyData.map((flight) => {
                const addFlights = {
                    flightFrom: journeyType === "helicopters" ? flight["flightFrom"] : flight["flightFrom"]["icao"],
                    flightTo: journeyType === "helicopters" ? flight["flightTo"] : flight["flightTo"]["icao"],
                    date: dateStringToApiFormat(flight["date"]),
                    time: getTime(flight["time"]),
                    numberOfPassengers: flight["passengers"],
                    itemsOfLuggage: flight["luggage"] ?? 0,
                    itemsOfCarryOn: flight["carryOn"] ?? 0,
                    journeyType: flight["journeyType"],
                };
                formattedJourneyData = [...formattedJourneyData, addFlights];
                return false;
            });

            // Set data
            let formData = {};
            const gclid = document.querySelector("#zc_gad");

            if (authCtx.token) {
                formData = {
                    journeys: formattedJourneyData,
                    id: authCtx.id,
                    additionalRequests: requests,
                    gclid: gclid ? gclid.value : null,
                    ...checkUtmProperties(),
                    tenantID: poweredByCtx.tenantID,
                };

                // Post
                postData(formData, "/api/flight/search-flights-by-member", 1000);
            } else {
                formData = {
                    journeys: formattedJourneyData,
                    firstName: firstName,
                    lastName: surname,
                    email: email,
                    phoneNumber: phone,
                    additionalRequests: requests,
                    currency: preferredCurrency,
                    contactPreferences: {
                        email: true,
                        whatsApp: contactPreferences["whatsApp"],
                    },
                    gdprConsentGiven: gdprConsentGiven,
                    gclid: gclid ? gclid.value : null,
                    ...checkUtmProperties(),
                    tenantID: poweredByCtx.tenantID,
                };

                // Post
                postData(formData, "/api/flight/search-flights", 1000);
            }
        }
    };

    // Remember journey data
    useEffect(() => {
        if (journeyIsValid && journeyData) {
            setRememberJourneyData(journeyData);
            setShowAllLegs(journeyData.length <= 3);
        }
    }, [journeyData, journeyIsValid]);

    // Reset journey on successful submit
    useEffect(() => {
        if (returnMessage.success && journeyIsValid) {
            TagManager.dataLayer({
                dataLayer: {
                    event: "/thank-you-for-your-request",
                },
            });
            resetJourney();
        }
    }, [journeyIsValid, resetJourney, returnMessage.success]);

    // Listen for window resize events
    useEffect(() => {
        let resizeTimeout;

        const resizeHandler = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                // Choose journey leg layout - using table layout (10) for tabular display
                setLayout(10);
            }, 100);
        };

        resizeHandler();
        window.addEventListener("resize", resizeHandler);

        return () => {
            window.removeEventListener("resize", resizeHandler);
        };
    }, [journeyType, rememberJourneyData.length]);

    let journeyDetails = [];
    if (rememberJourneyData && rememberJourneyData.length > 0) {
        if (layout === 10) {
            // Render all legs in a single table
            const legs = rememberJourneyData.map((journey) => ({
                fromLong: journeyType === "helicopters" ? journey.flightFrom : journey.flightFrom.displayName,
                fromShort: journeyType === "helicopters" ? null : journey.flightFrom.icao,
                toLong: journeyType === "helicopters" ? journey.flightTo : journey.flightTo.displayName,
                toShort: journeyType === "helicopters" ? null : journey.flightTo.icao,
                departureDate: dateStringToApiFormat(journey.date),
                departureTime: journey.time,
                luggage: journey.luggage,
                passengers: journey.passengers,
            }));

            journeyDetails = <QuoteQueryJourneyLeg legs={legs} />;
        } else {
            // Render individual legs for other layouts
            journeyDetails = rememberJourneyData.map((journey) => (
                <JourneyLeg
                    journeyType={journeyType}
                    layout={layout}
                    key={journey.id}
                    fromLong={journeyType === "helicopters" ? journey.flightFrom : journey.flightFrom.displayName}
                    fromShort={journeyType === "helicopters" ? null : journey.flightFrom.icao}
                    toLong={journeyType === "helicopters" ? journey.flightTo : journey.flightTo.displayName}
                    toShort={journeyType === "helicopters" ? null : journey.flightTo.icao}
                    departureDate={dateStringToApiFormat(journey.date)}
                    departureTime={journey.time}
                    luggage={journey.luggage}
                    passengers={journey.passengers}
                    useFlyingPlaneIcon
                    hideCountIcons
                    showCountIconsOnMobile
                />
            ));
        }
    }

    return (
        <div className="quote-query-personal-details">
            <div className="quote-query-personal-details__header">
                {journeyType === "helicopters" && (
                    <div className="quote-query-personal-details__header__legs-count">Helicopter</div>
                )}
                {journeyType === "group-charter" && (
                    <div className="quote-query-personal-details__header__legs-count">Group Charter</div>
                )}

                {legCount === 1 && <div className="quote-query-personal-details__header__legs-count">One Way</div>}
                {legCount === 2 &&
                    (isReturnTwoLegs() ? (
                        <div className="quote-query-personal-details__header__legs-count">{TRIP_TYPE_RETURN_TRIP}</div>
                    ) : (
                        <div className="quote-query-personal-details__header__legs-count">2 Legs Selected</div>
                    ))}
                {legCount > 2 && (
                    <div className="quote-query-personal-details__header__legs-count">{legCount} Legs Selected</div>
                )}

                {layout === 10 ? (
                    // For table layout, support collapse/expand for >3 legs
                    <>
                        <QuoteQueryJourneyLeg
                            legs={(rememberJourneyData || [])
                                .map((journey) => ({
                                    fromLong:
                                        journeyType === "helicopters"
                                            ? journey.flightFrom
                                            : journey.flightFrom.displayName,
                                    fromShort: journeyType === "helicopters" ? null : journey.flightFrom.icao,
                                    toLong:
                                        journeyType === "helicopters" ? journey.flightTo : journey.flightTo.displayName,
                                    toShort: journeyType === "helicopters" ? null : journey.flightTo.icao,
                                    departureDate: dateStringToApiFormat(journey.date),
                                    departureTime: journey.time,
                                    luggage: journey.luggage,
                                    passengers: journey.passengers,
                                }))
                                .slice(0, legCount > 3 && !showAllLegs ? 3 : undefined)}
                        />

                        {legCount > 3 && (
                            <button
                                className="quote-query-personal-details__header__more"
                                onClick={() => setShowAllLegs((v) => !v)}
                            >
                                <img src={chevronDown} alt="" />
                                <span>{showAllLegs ? "COLLAPSE ITINERARY" : "SEE FULL ITINERARY"}</span>
                                <span>{showAllLegs ? "COLLAPSE ITINERARY" : "SEE FULL ITINERARY"}</span>
                            </button>
                        )}
                    </>
                ) : (
                    <>
                        {Array.isArray(journeyDetails) && journeyDetails.slice(0, 2)}

                        {legCount > 2 && Array.isArray(journeyDetails) && (
                            <>
                                <SlideDown ref={slideDown} closed={!slideDownOpen}>
                                    {journeyDetails.slice(2)}
                                </SlideDown>

                                {slideDownOpen ? (
                                    <button
                                        onClick={() => setSlideDownOpen(!slideDownOpen)}
                                        className="quote-query-personal-details__header__more quote-query-personal-details__header__more--less"
                                    >
                                        <img src={chevronDown} alt="" />
                                        <span>Less legs</span>
                                        <span>See less legs</span>
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => setSlideDownOpen(!slideDownOpen)}
                                        className="quote-query-personal-details__header__more"
                                    >
                                        <img src={chevronDown} alt="" />
                                        <span>More legs</span>
                                        <span>See more legs</span>
                                    </button>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>

            <ReturnMessage returnMessage={returnMessage.success ? {} : returnMessage} inline />

            {(returnMessage.success || inModal) && (
                <div className="quote-query-personal-details__success">
                    <img src={bigTick} className="quote-query-personal-details__success__big-tick" alt="" />
                    <h2 className="quote-query-personal-details__success__thanks">Thank you for your request!</h2>
                    <div className="quote-query-personal-details__success__be-in-touch">We'll be in touch shortly</div>

                    <div className="quote-query-personal-details__new-quote-button-container">
                        <Button onClick={() => closeModal()} classNames={["button--full"]}>
                            {inModal ? "Close" : "New Request"}
                        </Button>
                    </div>
                    {!poweredByCtx.isPoweredBy && (
                        <div className="quote-query-personal-details__success__shy-lifestyle">
                            While you wait for your enquiry, have a look at{" "}
                            <a rel="noreferrer" target="_blank" href="https://www.shylifestyle.com">
                                Shy Lifestyle
                            </a>{" "}
                            for more luxury and comfort, before, during and after your flight.{" "}
                        </div>
                    )}
                </div>
            )}

            {!returnMessage.success && !inModal && (
                <div className="quote-query-personal-details__form">
                    <div className="quote-query-personal-details__intro">
                        {!authCtx.token
                            ? `Before we submit your request, please provide some more information:`
                            : `Thanks ${authCtx.firstName}, let us know if you have any other requests:`}
                    </div>

                    <Form classNames={authCtx.token && ["form--full"]} onSubmit={submitHandler}>
                        {!authCtx.token && (
                            <>
                                <TextInput
                                    id="first-name"
                                    validateForm={validateForm}
                                    label="First name*"
                                    required
                                    disabled={sending}
                                    maxLength={50}
                                    value={(value) => setFirstName(value)}
                                />

                                <TextInput
                                    id="surname"
                                    validateForm={validateForm}
                                    label="Surname*"
                                    required
                                    disabled={sending}
                                    maxLength={50}
                                    value={(value) => setSurname(value)}
                                />

                                <TextInput
                                    id="email"
                                    validateForm={validateForm}
                                    label="Email*"
                                    required
                                    validation="email"
                                    disabled={sending}
                                    maxLength={100}
                                    value={(value) => setEmail(value)}
                                />

                                <TextInput
                                    id="phone"
                                    validateForm={validateForm}
                                    label="Phone number*"
                                    required
                                    disabled={sending}
                                    maxLength={50}
                                    value={(value) => setPhone(value)}
                                />
                            </>
                        )}

                        <TextAreaInput
                            id="requests"
                            maxLength={2000}
                            showCharacterCount
                            label="Requests e.g. pets… (optional)"
                            disabled={sending}
                            value={(value) => setRequests(value)}
                        />

                        <div className="quote-query-personal-details__form-group">
                            {!authCtx.token && (
                                <>
                                    <SelectInput
                                        options={currencyList}
                                        value={(value) => setPreferredCurrency(value)}
                                        chevron={chevronDownBig}
                                        label="Preferred currency*"
                                        required
                                        disabled={sending}
                                        validateForm={validateForm}
                                        classNames={["select-input--full select-input--form"]}
                                    />

                                    <ContactPreferencesInput
                                        disabled={sending}
                                        value={(value) => setContactPreferences(value)}
                                    />
                                </>
                            )}
                        </div>

                        {!authCtx.token && (
                            <div className="quote-query-personal-details__text-area-container">
                                <Checkbox
                                    id="gdpr-consent"
                                    required
                                    value={(value) => setGdprConsentGiven(value)}
                                    validateForm={validateForm}
                                >
                                    I consent to the processing of my personal data in accordance with the{" "}
                                    <Link target="_blank" rel="noopener noreferrer" to={"/privacy-policy"}>
                                        {replaceName("Shy Aviation Privacy Policy")}
                                    </Link>
                                    .
                                </Checkbox>
                            </div>
                        )}

                        <div className="form-button-wrapper">
                            <Button disabled={sending} classNames={["button--full"]}>
                                Submit Request
                            </Button>
                        </div>
                    </Form>

                    {sending && <LoadingOverlay />}
                </div>
            )}
        </div>
    );
};

export default QuoteQueryPersonalDetails;
